import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {Button} from 'UI/components/action/Button';
import {Form} from 'UI/components/action/Form';
import {FormCheckboxInput} from 'UI/components/action/inputs/FormCheckboxInput';
import {TextAreaInput} from 'UI/components/action/inputs/TextAreaInput';
import {TextInput} from 'UI/components/action/inputs/TextInput';
import {BlockIntro} from 'UI/components/layout/BlockIntro';
import {H4, P} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';
import {HintAnimation} from 'UI/animations/textInput';

import {useContactBlock} from './useContactBlock';
import {EmailOutline, Geo, PhoneOutline} from 'Images/icons';
import {merge, required, validEmail, validPhone} from 'UI/utils/validations';

import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import type {ContactBlockType} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the properties for the `ContactBlock` component.
 * This interface specifies all the required and optional properties needed to configure a jobs block display.
 * It includes configuration for auto-filling job articles, manually selected job articles, block identification, and introductory content.
 */
interface ComponentProps {
    /**
     * The form configuration object containing all form field labels, messages, and display text.
     * This property defines the complete form structure including field labels, button text, success/error messages, and validation requirements.
     * The form configuration ensures consistent messaging and labeling across the contact form interface.
     */
    form: ContactBlockType['form'];
    /**
     * The unique identifier for this jobs block instance within the page or content management system.
     * This identifier is used for linking purposes, anchor navigation, and content management operations.
     * It serves as a reference point for block-specific configurations and targeting.
     */
    id: ContactBlockType['id'];
    /**
     * The introductory content configuration object containing title, description, and other presentation settings.
     * This object defines how the block introduction should be displayed including headings, descriptive text, and styling options.
     * The intro content appears at the top of the jobs block to provide context and enhance user experience.
     */
    intro: ContactBlockType['intro'];
    /**
     * Optional metadata associated with the page containing the blocks.
     * This property provides contextual information that blocks can utilize for enhanced rendering or behavior.
     * When not provided, blocks will operate with their default metadata handling.
     */
    metaData?: PageData['pageMetaData'];
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing.
     *
     * @default 'ContactBlock'
     */
    testId?: string;
}

// eslint-disable-next-line max-lines-per-function
const ContactBlock = observer(({
    form,
    id,
    intro,
    metaData,
    testId = 'ContactBlock'
}: ComponentProps) => {
    const {colors, error, handleSubmit, isLoading, isSend, orgaData} = useContactBlock(metaData);

    return (
        <BlockWrapper>
            <Container>
                <BlockIntro blockId={id} intro={intro} testId={testId} />
                <ViewportTrigger baseComponent={Wrapper}>
                    <FormWrapper variants={FadeInAnimation}>
                        <StyledForm id="contact" onSubmit={handleSubmit}>
                            <StyledHead as="h3">{form.headline}</StyledHead>
                            <TextInput label={form.nameLabel} name="name" validation={required} isRequired />
                            <TextInput label={form.companyLabel} name="company" />
                            <TextInput
                                label={form.emailLabel}
                                name="email"
                                type="email"
                                validation={merge([required, validEmail])}
                                isRequired
                            />
                            <TextInput label={form.phoneLabel} name="phone" type="tel" validation={validPhone} />
                            <StyledTextInput
                                label={form.messageLabel}
                                name="message"
                                validation={required}
                                isRequired
                            />
                            <FormCheckboxInputStyled initialValue={false} name="newsletter">
                                {form.newsletterLabel}
                            </FormCheckboxInputStyled>
                            <AnimatePresence initial={false} mode="sync">
                                {(isSend || error) && (
                                    <ExtraHint animate="enter" exit="exit" initial="initial" variants={HintAnimation}>
                                        {(isSend && !error) && (
                                            <P>
                                                <Color
                                                    $color={colors.tertiary}
                                                    aria-live="polite"
                                                >
                                                    {form.successMessage}
                                                </Color>
                                            </P>
                                        )}
                                        {error && (
                                            <P>
                                                <Color
                                                    $color={colors.errorColor}
                                                    aria-live="polite"
                                                >
                                                    {form.errorMessage}
                                                </Color>
                                            </P>
                                        )}
                                    </ExtraHint>
                                )}
                            </AnimatePresence>
                            <StyledButtonWrapper variants={FadeInAnimation}>
                                <Button
                                    as="button"
                                    isLoading={isLoading}
                                    loadingText="Wird versendet"
                                    type="submit"
                                    variant="primary"
                                >
                                    {form.buttonLabel}
                                </Button>
                            </StyledButtonWrapper>
                        </StyledForm>
                    </FormWrapper>
                    <ContactSideBar variants={FadeInAnimation}>
                        <H4 as="h3">Kontaktinformationen</H4>
                        <Spacer y={13} />
                        <ContactGrid>
                            <motion.div variants={FadeInAnimation}>
                                <StyledPhoneIcon height={19} width={19} />
                            </motion.div>
                            <motion.div variants={FadeInAnimation}>
                                <H4>Telefon</H4>
                                <Spacer y={2} />
                                <P>{orgaData?.telephone}</P>
                                {orgaData?.openingHours?.map(day => (
                                    <P key={day.times}>{day.times}</P>
                                ))}
                            </motion.div>
                            <motion.div variants={FadeInAnimation}>
                                <StyledEmailIcon />
                            </motion.div>
                            <motion.div variants={FadeInAnimation}>
                                <H4>E-Mail</H4>
                                <Spacer y={2} />
                                <P>{orgaData?.email}</P>
                            </motion.div>
                            <motion.div variants={FadeInAnimation}>
                                <Geo />
                            </motion.div>
                            <motion.div variants={FadeInAnimation}>
                                <H4>Adresse</H4>
                                <Spacer y={2} />
                                <P>{orgaData?.orgName}</P>
                                <P>
                                    {orgaData?.address?.streetAddress},{' '}
                                    {orgaData?.address?.postalCode} {orgaData?.address?.addressLocality}
                                </P>
                            </motion.div>
                        </ContactGrid>
                    </ContactSideBar>
                </ViewportTrigger>
            </Container>
        </BlockWrapper>
    );
});

ContactBlock.displayName = 'ContactBlock';

export {ContactBlock};

const BlockWrapper = styled.div``;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};

    ${media('lg')} {
        flex-direction: row;
    }
`;

const FormWrapper = styled(motion.div)`
    background-color: ${({theme}) => theme.colors.backgroundLight};
    border-radius: 16px;
    column-gap: ${spacing(7)};
    display: grid;
    flex: 1 1 calc(64% - 12px);
    grid-template-columns: 1fr;
    padding: ${spacing(7)} ${spacing(6)};
    row-gap: ${spacing(7)};
    transition: opacity 0.3s ease-in-out;

    ${media('sm')} {
        grid-template-columns: 1fr 1fr;
        padding: ${spacing(14)} ${spacing(13)};
    }
`;

const StyledForm = styled(Form)`
    display: contents;
`;

const StyledHead = styled(H4)`
    grid-column: 1 / -1;
`;

const StyledTextInput = styled(TextAreaInput)`
    grid-column: 1 / -1;
`;

const FormCheckboxInputStyled = styled(FormCheckboxInput)`
    grid-column: 1 / -1;
`;

const StyledButtonWrapper = styled(motion.div)`
    grid-column: 1 / -1;
`;

const ExtraHint = styled(motion.div)`
    grid-column: 1 / -1;
    height: 0;
    overflow: hidden;

    & p {
        font-size: 1.2rem;

        ${media('md')} {
            font-size: 1.4rem;
        }
    }
`;

const ContactSideBar = styled(motion.div)`
    align-self: start;
    flex: 1 1 calc(36% - 12px);

    ${media('lg')} {
        align-self: center;
    }
`;

const ContactGrid = styled.div`
    display: grid;
    grid-template-columns: 3.6rem 1fr;
    row-gap: ${spacing(3)};

    ${media('sm')} {
        grid-template-columns: 3.6rem 1fr 3.6rem 1fr;
    }

    ${media('md')} {
        grid-template-columns: 3.6rem 1fr 3.6rem 1fr 3.6rem 1fr;
    }

    ${media('lg')} {
        grid-template-columns: 3.6rem 1fr;
    }
`;

const StyledPhoneIcon = styled(PhoneOutline)`
    position: relative;
    top: 2px;
`;

const StyledEmailIcon = styled(EmailOutline)`
    position: relative;
    top: 4px;
`;